import type { EditorState } from "@codemirror/state";
import { useEffect, useState } from "react";
import type { Descendant, Editor } from "slate";
import type { History } from "slate-history";

import type { IChoice } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { isJSON } from "../../../../utils/isJSON";

export interface IChoiceState extends IChoice {
  history: History | null | string;
  unsavedChanges: Descendant[] | null;
  jsonUnsavedChanges?: string | null;
  isNew?: boolean;
  isJSON: boolean;
  jsonState?: EditorState | null;
}
export const useChoicesState = (choices: IChoice[]) => {
  const [choicesState, setChoicesState] = useState<IChoiceState[]>([]);
  const [currentChoice, setCurrentChoice] = useState<number>(1);

  useEffect(() => {
    const choicesState = choices.map(choice => {
      return {
        ...choice,
        startValue: isJSON(choice.message.content)
          ? JSON.parse(choice.message.content)
          : choice.message.content,
        history: null,
        unsavedChanges: null,
        isJSON: isJSON(choice.message.content),
        jsonUnsavedChanges: null,
      };
    });
    setChoicesState(choicesState);
  }, [choices]);

  const setChoiceAsSaved = (choice: IChoice) => {
    setChoicesState(prev => {
      return prev.map((choiceState, i) => {
        if (currentChoice - 1 === i) {
          return {
            ...choice,
            history: null,
            unsavedChanges: null,
            jsonUnsavedChanges: null,
            isJSON: choiceState.isJSON,
            jsonState: null,
          };
        }
        return choiceState;
      });
    });
  };
  const handleSlateChoiceChange = (
    hasChanges: boolean,
    editor: Editor,
    choice?: number,
  ) => {
    setChoicesState(prev => {
      const state: IChoiceState[] =
        prev.map((choiceState, index) => {
          if (index === currentChoice - 1) {
            return {
              ...choiceState,
              history: hasChanges ? editor?.history : null,
              unsavedChanges: hasChanges ? editor?.children : null,
              isNew: false,
            };
          }
          return choiceState;
        }) || [];
      if (!choice) {
        state.push({
          index: prev.length,
          finish_reason: "",
          history: null,
          unsavedChanges: null,
          isNew: true,
          id: "",
          message: {
            content: "",
            role: "user",
          },
          isJSON: false,
        });
      }
      return state;
    });
    if (choice) {
      setCurrentChoice(prev => {
        return choice > 0
          ? prev === choicesState.length
            ? 1
            : prev + choice
          : prev === 1
          ? choicesState.length
          : prev + choice;
      });
      return;
    }
    setCurrentChoice(choicesState.length + 1);
  };

  const handleJsonChoiceChange = (
    hasChanges: boolean,
    changes: EditorState,
    choice: number,
  ) => {
    const newValue = changes.toJSON().doc;
    setChoicesState(prev => {
      const state: IChoiceState[] =
        prev.map((choiceState, index) => {
          if (index === currentChoice - 1) {
            return {
              ...choiceState,
              history: null,
              unsavedChanges: null,
              jsonUnsavedChanges: hasChanges ? JSON.stringify(newValue) : null,
              isNew: false,
              jsonState: changes,
            };
          }
          return choiceState;
        }) || [];
      return state;
    });

    setCurrentChoice(prev => {
      return choice > 0
        ? prev === choicesState.length
          ? 1
          : prev + choice
        : prev === 1
        ? choicesState.length
        : prev + choice;
    });
  };

  const removeAnswer = () => {
    if (choicesState.length === 1) {
      return;
    }

    setChoicesState(prev => {
      return prev.filter((_choice, i) => {
        return i !== currentChoice - 1;
      });
    });
    if (currentChoice === choicesState.length) {
      setCurrentChoice(prev => {
        return prev - 1;
      });
    }
  };

  return {
    currentChoice,
    choicesState,
    handleSlateChoiceChange,
    handleJsonChoiceChange,
    removeAnswer,
    setChoiceAsSaved,
  };
};
